import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { userApi, customerApi, areaPositionApi } from '../../services/api'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

/**
 * Users admin — CRUD over the login accounts.
 *
 * Includes the multi-name model: when an account has a customer (any orderer),
 * an optional "Lit Rep Name" appears. When is_gsr is ticked, a "GSR Name"
 * field appears. When is_area_admin is ticked, an "Area Admin Name" field
 * appears alongside the existing position dropdown.
 *
 * All three name fields are optional. If blank, forms throughout the app
 * fall back to the account's primary name. This supports the shared-login
 * model where one homegroup login (e.g. keytofreedom@) can submit orders,
 * GSR reports, and area reports with the right signer on each.
 */
export default function Users() {
    const [users, setUsers] = useState([])
    const [customers, setCustomers] = useState([])
    const [positions, setPositions] = useState([])

    const [loading, setLoading] = useState(true)
    const [includeDisabled, setIncludeDisabled] = useState(false)

    const [editing, setEditing] = useState(null)        // null | {} | {...user}
    const [resetTarget, setResetTarget] = useState(null) // null | user

    useEffect(() => { loadCustomers(); loadPositions() }, [])
    useEffect(() => { loadUsers() }, [includeDisabled])

    async function loadUsers() {
        setLoading(true)
        try {
            const response = await userApi.list(includeDisabled)
            if (response.success) setUsers(response.users)
            else toast.error(response.message || 'Could not load users')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function loadCustomers() {
        try {
            const response = await customerApi.list()
            if (response.success) setCustomers(response.customers)
        } catch { /* non-fatal */ }
    }

    async function loadPositions() {
        try {
            const response = await areaPositionApi.list()
            if (response.success) setPositions(response.positions)
        } catch { /* non-fatal */ }
    }

    async function handleDeactivate(user) {
        if (!confirm(`Disable login for "${user.name}"?`)) return
        try {
            const response = await userApi.deactivate(user.id)
            if (response.success) {
                toast.success('User deactivated')
                loadUsers()
            } else {
                toast.error(response.message || 'Deactivation failed')
            }
        } catch {
            toast.error('Network error')
        }
    }

    async function handleReactivate(user) {
        try {
            const response = await userApi.update(user.id, {
                name: user.name,
                email: user.email,
                phone: user.phone,
                city: user.city,
                litRepName: user.litRepName,
                gsrName: user.gsrName,
                areaAdminName: user.areaAdminName,
                customerId: user.customerId,
                isSysAdmin: user.isSysAdmin,
                isAreaAdmin: user.isAreaAdmin,
                isGsr: user.isGsr,
                canUpload: user.canUpload,
                areaPosition: user.areaPosition,
                enabled: true,
            })
            if (response.success) {
                toast.success(`${user.name} reactivated`)
                loadUsers()
            } else {
                toast.error(response.message || 'Reactivation failed')
            }
        } catch {
            toast.error('Network error')
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Users</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Login accounts and permissions. Roles control what each user can do.
                    </p>
                </div>
                <Button onClick={() => setEditing({})}>+ New User</Button>
            </div>

            {/* Show-disabled toggle */}
            <div className="flex justify-end">
                <label className="flex items-center text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={includeDisabled}
                        onChange={(e) => setIncludeDisabled(e.target.checked)}
                        className="mr-2"
                    />
                    Show disabled
                </label>
            </div>

            {/* List */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : users.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">No users yet.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {users.map(u => (
                        <div key={u.id} className={`bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3 ${u.enabled ? '' : 'opacity-60'}`}>
                            {/* Top row: name + disabled flag */}
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="font-semibold text-gray-900 break-words">{u.name}</div>
                                    <div className="text-xs text-gray-500 break-all">{u.email}</div>
                                </div>
                                {!u.enabled && (
                                    <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        Disabled
                                    </span>
                                )}
                            </div>

                            {/* Customer + roles */}
                            <div className="space-y-2">
                                {u.customerName && (
                                    <div className="text-sm">
                                        <span className="text-xs uppercase tracking-wider text-gray-500">Customer: </span>
                                        <span className="text-gray-900">{u.customerName}</span>
                                    </div>
                                )}
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Roles</div>
                                    <RoleBadges user={u} />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                                <button
                                    onClick={() => setEditing(u)}
                                    className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => setResetTarget(u)}
                                    className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                                >
                                    Reset Pwd
                                </button>
                                {u.enabled ? (
                                    <button
                                        onClick={() => handleDeactivate(u)}
                                        className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100"
                                    >
                                        Disable
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleReactivate(u)}
                                        className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-success bg-green-50 rounded-lg hover:bg-green-100"
                                    >
                                        Reactivate
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing !== null && (
                <UserEditModal
                    user={editing}
                    customers={customers}
                    positions={positions}
                    onPositionsChanged={loadPositions}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); loadUsers() }}
                />
            )}

            {resetTarget && (
                <ResetPasswordModal
                    user={resetTarget}
                    onClose={() => setResetTarget(null)}
                />
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Role badges (small visual indicator)
// ---------------------------------------------------------------------------
function RoleBadges({ user }) {
    const roles = []
    if (user.isSysAdmin) roles.push({ label: 'Sysadmin', color: 'bg-red-100 text-red-800' })
    if (user.isAreaAdmin) roles.push({
        label: user.areaPosition ? `Area: ${user.areaPosition}` : 'Area Admin',
        color: 'bg-blue-100 text-blue-800',
    })
    if (user.isGsr) roles.push({ label: 'GSR', color: 'bg-green-100 text-green-800' })
    if (user.canUpload) roles.push({ label: 'Upload', color: 'bg-purple-100 text-purple-800' })

    if (roles.length === 0) return <span className="text-gray-400">—</span>
    return (
        <div className="flex flex-wrap gap-1">
            {roles.map((r, i) => (
                <span key={i} className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${r.color}`}>
                    {r.label}
                </span>
            ))}
        </div>
    )
}

// ===========================================================================
//  Edit / Create modal
// ===========================================================================
function UserEditModal({ user, customers, positions, onPositionsChanged, onClose, onSaved }) {
    const isNew = !user.id
    const [form, setForm] = useState(() => ({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        city: user.city || '',
        password: '',                       // only used for new users
        customerId: user.customerId || '',

        // Multi-name fields
        litRepName: user.litRepName || '',
        gsrName: user.gsrName || '',
        areaAdminName: user.areaAdminName || '',

        isSysAdmin: !!user.isSysAdmin,
        isAreaAdmin: !!user.isAreaAdmin,
        isGsr: !!user.isGsr,
        canUpload: !!user.canUpload,
        areaPosition: user.areaPosition || '',
        enabled: isNew ? true : (user.enabled !== false),
    }))
    const [busy, setBusy] = useState(false)

    function update(field, value) {
        setForm(f => ({ ...f, [field]: value }))
    }

    const hasCustomer = !!form.customerId

    async function handleSave(e) {
        e.preventDefault()

        if (!form.name.trim() || !form.email.trim()) {
            toast.error('Name and email are required')
            return
        }
        if (isNew && !form.password) {
            toast.error('Password is required for new users')
            return
        }
        if (form.isAreaAdmin && !form.areaPosition) {
            toast.error('Please pick an area position')
            return
        }

        setBusy(true)
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim() || null,
                city: form.city.trim() || null,
                customerId: form.customerId === '' ? null : Number(form.customerId),
                litRepName: hasCustomer && form.litRepName.trim() ? form.litRepName.trim() : null,
                gsrName: form.isGsr && form.gsrName.trim() ? form.gsrName.trim() : null,
                areaAdminName: form.isAreaAdmin && form.areaAdminName.trim() ? form.areaAdminName.trim() : null,
                isSysAdmin: form.isSysAdmin,
                isAreaAdmin: form.isAreaAdmin,
                isGsr: form.isGsr,
                canUpload: form.canUpload,
                areaPosition: form.isAreaAdmin && form.areaPosition ? form.areaPosition.trim() : null,
                enabled: form.enabled,
            }
            if (isNew) payload.password = form.password

            const response = isNew
                ? await userApi.create(payload)
                : await userApi.update(user.id, payload)

            if (response.success) {
                toast.success(isNew ? 'User created' : 'User updated')
                onSaved()
            } else {
                toast.error(response.message || 'Save failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handlePositionChange(value) {
        if (value === '__new__') {
            const name = prompt('New position name:')
            if (!name) return
            try {
                const response = await areaPositionApi.create({ name: name.trim() })
                if (response.success) {
                    toast.success(`Added "${response.position.name}"`)
                    update('areaPosition', response.position.name)
                    onPositionsChanged()
                } else {
                    toast.error(response.message || 'Could not add position')
                }
            } catch {
                toast.error('Network error')
            }
            return
        }
        update('areaPosition', value)
    }

    const positionOptionList = [...positions]
    const userHasLegacyPosition = form.areaPosition
        && !positions.find(p => p.name === form.areaPosition)
    if (userHasLegacyPosition) {
        positionOptionList.push({ id: 'legacy', name: form.areaPosition, _legacy: true })
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <form onSubmit={handleSave} className="bg-white rounded-xl shadow-xl w-full max-w-xl my-8">
                <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">
                        {isNew ? 'New User' : `Edit ${user.name}`}
                    </h3>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input id="u-name" label="Name" value={form.name} onChange={(e) => update('name', e.target.value)} required />
                        <Input id="u-email" label="Email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input id="u-phone" label="Phone (optional)" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                        <Input id="u-city" label="City (optional)" value={form.city} onChange={(e) => update('city', e.target.value)} />
                    </div>

                    {isNew && (
                        <Input id="u-password" label="Initial password" type="password"
                            value={form.password} onChange={(e) => update('password', e.target.value)} required />
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer / Homegroup</label>
                        <select
                            value={form.customerId}
                            onChange={(e) => update('customerId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                        >
                            <option value="">— None (not linked) —</option>
                            {customers.map(c => (
                                <option key={c.id} value={c.id}>{c.name} ({c.kind})</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Required for placing orders or submitting reports.
                        </p>
                    </div>

                    {/* Lit Rep Name — shown when user has a customer */}
                    {hasCustomer && (
                        <Input
                            id="u-lit-rep-name"
                            label="Lit Rep Name (optional)"
                            placeholder={`Defaults to "${form.name || 'the account name'}" if blank`}
                            value={form.litRepName}
                            onChange={(e) => update('litRepName', e.target.value)}
                        />
                    )}

                    {/* Roles ----- */}
                    <div className="border-t border-gray-100 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                        <div className="space-y-2">
                            <RoleCheckbox checked={form.isSysAdmin} onChange={(v) => update('isSysAdmin', v)} label="Sysadmin"
                                hint="Full admin access — manage users, customers, inventory, config." />
                            <RoleCheckbox checked={form.isGsr} onChange={(v) => update('isGsr', v)} label="GSR"
                                hint="Group Service Representative — can submit monthly GSR reports." />
                            <RoleCheckbox checked={form.isAreaAdmin} onChange={(v) => update('isAreaAdmin', v)} label="Area Officer"
                                hint="Treasurer, Secretary, etc. — can submit area officer reports." />
                            <RoleCheckbox checked={form.canUpload} onChange={(v) => update('canUpload', v)} label="Inventory Upload"
                                hint="Can add/edit/import inventory items even if not a sysadmin." />
                        </div>
                    </div>

                    {/* GSR Name — shown when isGsr is ticked */}
                    {form.isGsr && (
                        <Input
                            id="u-gsr-name"
                            label="GSR Name (optional)"
                            placeholder={`Defaults to "${form.name || 'the account name'}" if blank`}
                            value={form.gsrName}
                            onChange={(e) => update('gsrName', e.target.value)}
                        />
                    )}

                    {/* Area Officer details — shown when isAreaAdmin is ticked */}
                    {form.isAreaAdmin && (
                        <>
                            <Input
                                id="u-area-admin-name"
                                label="Area Admin Name (optional)"
                                placeholder={`Defaults to "${form.name || 'the account name'}" if blank`}
                                value={form.areaAdminName}
                                onChange={(e) => update('areaAdminName', e.target.value)}
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Area position <span className="text-ghasc-danger">*</span>
                                </label>
                                <select
                                    value={form.areaPosition}
                                    onChange={(e) => handlePositionChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                >
                                    <option value="">— Select position —</option>
                                    {positionOptionList.map(p => (
                                        <option key={p.id} value={p.name}>
                                            {p.name}{p._legacy ? ' (legacy)' : ''}
                                        </option>
                                    ))}
                                    <option value="__new__">+ New position…</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Manage the full list in Admin Config → Area Officer Positions.
                                </p>
                            </div>
                        </>
                    )}

                    {/* Account enabled (existing users only) */}
                    {!isNew && (
                        <div className="border-t border-gray-100 pt-4">
                            <label className="flex items-center text-sm text-gray-900 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.enabled}
                                    onChange={(e) => update('enabled', e.target.checked)}
                                    className="mr-2"
                                />
                                Account enabled (can log in)
                            </label>
                            {!form.enabled && (
                                <p className="text-xs text-amber-700 mt-1">
                                    Unchecking will disable this user's login.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="submit" loading={busy}>{isNew ? 'Create' : 'Save'}</Button>
                    </div>
                </div>
            </form>
        </div>
    )
}

function RoleCheckbox({ checked, onChange, label, hint }) {
    return (
        <label className="flex items-start gap-2 cursor-pointer">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5"
            />
            <div className="flex-1">
                <div className="text-sm text-gray-900">{label}</div>
                {hint && <div className="text-xs text-gray-500">{hint}</div>}
            </div>
        </label>
    )
}

// ===========================================================================
//  Reset password modal
// ===========================================================================
function ResetPasswordModal({ user, onClose }) {
    const [pw1, setPw1] = useState('')
    const [pw2, setPw2] = useState('')
    const [busy, setBusy] = useState(false)

    async function handleSubmit(e) {
        e.preventDefault()
        if (pw1.length < 6) {
            toast.error('Password must be at least 6 characters')
            return
        }
        if (pw1 !== pw2) {
            toast.error('Passwords don\'t match')
            return
        }
        setBusy(true)
        try {
            const response = await userApi.resetPassword(user.id, pw1)
            if (response.success) {
                toast.success('Password reset')
                onClose()
            } else {
                toast.error(response.message || 'Reset failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">Reset Password</h3>
                    <p className="text-sm text-gray-600">For: <span className="font-medium text-gray-900">{user.name}</span></p>

                    <Input id="pw1" label="New password" type="password"
                        value={pw1} onChange={(e) => setPw1(e.target.value)} required />
                    <Input id="pw2" label="Confirm password" type="password"
                        value={pw2} onChange={(e) => setPw2(e.target.value)} required />

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="submit" loading={busy}>Reset Password</Button>
                    </div>
                </div>
            </form>
        </div>
    )
}